import { FC, useCallback, useEffect, useState } from 'react';
import { useRouteMatch, useLocation, useHistory } from 'react-router-dom';
import toast from 'react-hot-toast';

import {
  getVotedPollInfo,
  voteAnother,
  votePoll,
} from '@sdk/managers/VotesManager';
import { getPollDetail } from '@sdk/managers/PollsManager';
import { formatPollData, FormattedPoll } from '@utils/Poll';
import { useAuth } from '@hooks/Auth';

import HeaderImage from '@components/elements/HeaderImage';
import ButtonMighty from '@components/elements/ButtonMighty';
import PollOptionCard from '@components/elements/PollOptionCard';
import PollShareCard from '@components/modules/PollShareCard';

import SuccessModal from './SucessModal';

import './PollVotePage.css';
import { Poll, VoteResult } from '@sdk/models';

const PollVotePage: FC = () => {
  const {
    params: { pollId },
  } = useRouteMatch<{
    pollId: string;
  }>();
  const { search } = useLocation();
  const history = useHistory<any>();
  const params = new URLSearchParams(search);
  const action = params.get('action');
  const option = params.get('option');

  const { dbUser } = useAuth();
  const [loading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<FormattedPoll>();
  const [, setVoted] = useState<number>();
  const [selected, setSelected] = useState<string>();
  const [modal, setModal] = useState<boolean>(false);

  const fetchPollDetails = useCallback(async () => {
    try {
      const res: any = await getPollDetail(
        [
          'id',
          'title',
          'question',
          'description',
          'owner',
          'createdAt',
          'image { key bucket region }',
          'video',
          'status',
          {
            company: ['name'],
          },
          'hasPledge',
          'startTime',
          'endTime',
          'pledgePeriod1StartTime',
          'pledgePeriod1EndTime',
          'pledgePeriod2StartTime',
          'pledgePeriod2EndTime',
          'hasPledge',
          {
            options: [
              'id',
              'title',
              'description',
              { detail: ['image { key bucket region }', 'video'] },
            ],
            winOption: [
              'id',
              'title',
              'description',
              { detail: ['image { key bucket region }', 'video'] },
            ],
          },
          { voteResults: [{ items: ['id', 'count'] }] },
        ],
        pollId
      );

      let poll: (Poll & { voteResults?: { items: VoteResult[] } }) | undefined =
        res.data?.result;
      if (!poll) throw new Error('Failed to fetch poll.');

      setData(await formatPollData(poll));
    } catch (err: any) {
      console.error(err);
      toast.error(err.message);
    }
  }, [pollId]);

  const fetchPollVoted = useCallback(async () => {
    try {
      if (!dbUser) throw new Error('User not found.');

      const res: any = await getVotedPollInfo(
        [{ items: [{ option: ['id'] }] }],
        dbUser.id,
        pollId
      );

      const votedItems = res.data?.result?.items;
      if (!votedItems || votedItems.length === 0) {
        setVoted(-1);
      }

      setVoted(votedItems?.[0]?.option?.id);
    } catch (err: any) {
      console.error(err);
      toast.error(err.message);
    }
  }, [pollId, dbUser]);

  const init = useCallback(async () => {
    setLoading(true);

    await fetchPollDetails();

    setLoading(false);
  }, [fetchPollDetails]);

  useEffect(() => {
    init();
  }, [init]);

  useEffect(() => {
    if (dbUser) fetchPollVoted();
  }, [dbUser, fetchPollVoted]);

  useEffect(() => {
    if (!search || search === '') return;
    if (action === 'edit' && option) {
      setSelected(option);
    }
  }, [action, option, search]);

  const onSubmit = useCallback(async () => {
    if (!selected) return;

    setLoading(true);

    const submitVote = action === 'edit' ? voteAnother : votePoll;
    const guest = localStorage.getItem('mighty_poll_guest');
    try {
      if (!dbUser) throw new Error('User not found.');

      await submitVote(
        ['id'],
        pollId,
        null,
        dbUser.id,
        selected,
        guest ? true : false
      );
      setModal(true);
    } catch (err: any) {
      try {
        if (!dbUser) throw new Error('User not found.');

        await voteAnother(
          ['id'],
          pollId,
          null,
          dbUser.id,
          selected,
          guest ? true : false
        );
        setModal(true);
      } catch (err: any) {
        console.error('vote another failed', err);
        toast.error(err.errors?.[0]?.message || err.message);
      }
    }

    setLoading(false);
  }, [action, dbUser, pollId, selected]);

  return (
    <>
      <SuccessModal visible={modal} onSubmit={() => history.replace(`/home`)} />
      <div className="poll-vote-page">
        <HeaderImage
          title={data?.question || ''}
          titleSize={(data?.question?.length || 0) > 15 ? 40 : 55}
          image={data?.image?.url || 'image'}
          alt="profile picture"
        />
        <div className="poll-vote-response-background">
          <div className="poll-vote-response-container">
            <h2>Choose your response</h2>
            {!data
              ? [0, 1].map((_, i) => (
                  <PollOptionCard key={`poll-option-${i}`} title="" />
                ))
              : data.options?.map((option, i) => (
                  <PollOptionCard
                    key={`poll-option-${i}`}
                    active={option.id === selected}
                    onClick={() => setSelected(option.id)}
                    title={option.title || `Option ${i}`}
                    description={option.description}
                    image={option.detail?.image?.url}
                    video={option.detail?.video}
                  />
                ))}
          </div>
        </div>
        <div className="poll-detail-action-container">
          <div>
            <ButtonMighty
              onClick={onSubmit}
              disabled={loading}
              loading={loading}
              color="#ff001e"
              borderColor="#5600fb"
              textColor="#FFF"
              text={action === 'edit' ? 'Save vote' : 'Vote'}
              margin="0"
            />
          </div>
        </div>

        {data && (
          <div className="poll-detail-votes">
            <div>{data.voteCount}</div>
            <div>Votes</div>
          </div>
        )}

        <PollShareCard
          pollId={pollId}
          owner={data?.company?.name || data?.owner || undefined}
        />

        <div className="poll-detail-report">Report Poll</div>
      </div>
    </>
  );
};

export default PollVotePage;
